import type { Updater } from '@tanstack/react-table';

import type { TableState } from '../../shared/hooks/useTableState';

export interface UseTableStateHandlersOptions {
  tableState: TableState | undefined;
  initialState: TableState;
  updateState: (updates: Partial<TableState>) => void;
}

export interface UseTableStateHandlersReturn {
  onColumnFiltersChange: (updater: Updater<TableState['columnFilters']>) => void;
  onColumnOrderChange: (updater: Updater<TableState['columnOrder']>) => void;
  onColumnVisibilityChange: (updater: Updater<TableState['columnVisibility']>) => void;
  onGlobalFilterChange: (updater: Updater<TableState['globalFilter']>) => void;
  onPaginationChange: (updater: Updater<TableState['pagination']>) => void;
  onSortingChange: (updater: Updater<TableState['sorting']>) => void;
}

function createStateHandler<K extends keyof TableState>(
  stateKey: K,
  tableState: TableState | undefined,
  initialState: TableState,
  updateState: (updates: Partial<TableState>) => void,
) {
  return (updater: Updater<TableState[K]>) => {
    const currentValue = tableState?.[stateKey] ?? initialState[stateKey];
    const newValue = typeof updater === 'function' ? updater(currentValue) : updater;
    updateState({ [stateKey]: newValue } as Partial<TableState>);
  };
}

export function useTableStateHandlers({
  tableState,
  initialState,
  updateState,
}: UseTableStateHandlersOptions): UseTableStateHandlersReturn {
  const onColumnFiltersChange = createStateHandler('columnFilters', tableState, initialState, updateState);
  const onColumnOrderChange = createStateHandler('columnOrder', tableState, initialState, updateState);
  const onColumnVisibilityChange = createStateHandler('columnVisibility', tableState, initialState, updateState);
  const onGlobalFilterChange = createStateHandler('globalFilter', tableState, initialState, updateState);

  const onPaginationChange = (updater: Updater<TableState['pagination']>) => {
    const currentValue = tableState?.pagination ?? initialState.pagination;
    const newValue = typeof updater === 'function' ? updater(currentValue) : updater;
    updateState({ pagination: newValue } as Partial<TableState>);
  };

  const onSortingChange = createStateHandler('sorting', tableState, initialState, updateState);

  return {
    onColumnFiltersChange,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onGlobalFilterChange,
    onPaginationChange,
    onSortingChange,
  };
}
